import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { Cookies } from 'react-cookie';

import { fetchLogout } from '../pages/Home/store-utils';
import { changeSearchAction, changeSearchLocationAction } from '../store/projects/actions';
import { dispatch } from '../store';

let isRefreshing = false;

export const cookies = new Cookies();

// Bare Axios instance (no interceptors) for refresh requests
const bareAxios = axios.create();

const refreshSubscribers: (() => void)[] = [];

export const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const accessToken = cookies.get('ciid_access_token');
    if (accessToken && config.headers) {
      config.headers['Authorization'] = 'Bearer ' + accessToken;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const { config, response } = error;
    const originalRequest = config as any;
    const status = response?.status;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        return refreshAccessToken()
          .then(() => {
            isRefreshing = false;
            onRefreshed();
            return api(originalRequest);
          })
          .catch((err) => {
            isRefreshing = false;
            console.error('Token refresh failed');
            cookies.set('ciid_access_token', '');
            cookies.set('ciid_refresh_token', '');
            return Promise.resolve(err);
          });
      }

      return new Promise((resolve) => {
        subscribeTokenRefresh(() => {
          resolve(api(originalRequest));
        });
      });
    }

    console.error(`${originalRequest?.url} request failed with status ${status}`);
    return Promise.reject(error);
  },
);

export const handleLogout = async () => {
  dispatch(changeSearchLocationAction(null));
  dispatch(changeSearchAction(''));
  await fetchLogout();
};

const subscribeTokenRefresh = (cb: () => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = () => {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers.length = 0;
};

async function refreshAccessToken(): Promise<string> {
  const refreshToken = cookies.get('ciid_refresh_token');

  if (!refreshToken) {
    handleLogout();
    throw new Error('No refresh token available');
  }

  try {
    const response = await bareAxios.post(
      `${api.defaults.baseURL}/refresh-token`,
      { refresh_token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      },
    );

    const { ciid_access_token, ciid_refresh_token } = response.data;

    cookies.set('ciid_access_token', ciid_access_token);
    cookies.set('ciid_refresh_token', ciid_refresh_token);

    return ciid_access_token;
  } catch (err) {
    handleLogout();
    throw err;
  }
}
